import React from 'react';
import { BP_TOOLBAR_BUTTON_SMALL_CLASSES } from './brandPresenceClasses';
import { resumeStatsRequests } from './statsRequestQueue';

/**
 * How many times the reader asked a section to try again, and the callback that asks once more.
 * An effect that lists the count among its dependencies runs its request anew on every click.
 *
 * Asking again also ends the pause a failed statistics request began (`resumeStatsRequests`). It
 * ends from an effect of the same render as the section's own, and the queue decides what to send
 * only once they all ran, so the section's request takes its turn by priority among the ones that
 * waited rather than behind them.
 */
export function useRetry(): [number, () => void] {
  const [attempt, setAttempt] = React.useState(0);
  React.useEffect(() => {
    if (attempt) {
      resumeStatsRequests();
    }
  }, [attempt]);
  const retry = React.useCallback(() => setAttempt((current) => current + 1), []);
  return [attempt, retry];
}

/**
 * What a section shows in place of its figures when their request failed: what went wrong, and a
 * button that asks again. Without it a failed request reads as a section with nothing to show, or
 * as one that is still loading.
 */
export function SectionLoadFailed({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div
      role="alert"
      className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
    >
      <span>{message}</span>
      <button type="button" className={BP_TOOLBAR_BUTTON_SMALL_CLASSES} onClick={onRetry}>
        Try again
      </button>
    </div>
  );
}

/** Said above the headline figures of an analytics screen when they could not be loaded. */
export const KPIS_LOAD_FAILED_MESSAGE = 'The headline figures could not be loaded.';

/**
 * Said when a section's history could not be loaded: in place of a trend chart, or above a table
 * whose trend column it fills.
 */
export const TRENDS_LOAD_FAILED_MESSAGE = 'The trends could not be loaded.';
